import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Compass, Tv, Film } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Discover() {
  const { user, getToken } = useAuth();

  const [mediaType, setMediaType] = useState('tv');
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [genresLoading, setGenresLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [onlyMyServices, setOnlyMyServices] = useState(false);

  const fetchGenres = useCallback(async (type) => {
    setGenresLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API}/tmdb/genres`, {
        params: { media_type: type },
        headers: { Authorization: `Bearer ${token}` },
      });
      setGenres(res.data.genres || []);
      setSelectedGenre(null);
    } catch {}
    setGenresLoading(false);
  }, [getToken]);

  const fetchResults = useCallback(async (genreId, type, pageNum = 1, myServices = false) => {
    if (pageNum === 1) setLoading(true); else setLoadingMore(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API}/tmdb/discover`, {
        params: { genre_id: genreId || '', media_type: type, page: pageNum, only_my_services: myServices },
        headers: { Authorization: `Bearer ${token}` },
      });
      const newItems = res.data.results || [];
      if (pageNum === 1) setResults(newItems); else setResults(prev => [...prev, ...newItems]);
      setHasMore(newItems.length === 20);
      setPage(pageNum);
    } catch {}
    if (pageNum === 1) setLoading(false); else setLoadingMore(false);
  }, [getToken]);

  useEffect(() => {
    if (user) fetchGenres(mediaType);
  }, [user, mediaType, fetchGenres]);

  useEffect(() => {
    if (user) fetchResults(selectedGenre?.id || '', mediaType, 1, onlyMyServices);
  }, [user, selectedGenre, mediaType, onlyMyServices, fetchResults]);

  const switchType = (type) => {
    setLoading(true);
    setMediaType(type);
    setSelectedGenre(null);
    setPage(1);
    setHasMore(true);
  };

  const toggleMyServices = () => {
    setLoading(true);
    setPage(1);
    setHasMore(true);
    setOnlyMyServices(v => !v);
  };

  return (
    <div className="max-w-5xl mx-auto pb-20">
      <div className="pt-2 pb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Outfit' }}>
            Discover
          </h1>
          <p className="text-neutral-500 text-sm mt-1">Browse by genre</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleMyServices}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${
              onlyMyServices
                ? 'bg-[#4A9EFF]/20 border-[#4A9EFF]/40 text-[#4A9EFF]'
                : 'bg-[#0A0A0A] border-white/5 text-neutral-400 hover:text-white hover:border-white/20'
            }`}
          >
            My services
          </button>
          <div className="flex items-center gap-1 p-1 rounded-xl bg-[#0A0A0A] border border-white/5">
            <button
              onClick={() => switchType('tv')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                mediaType === 'tv' ? 'bg-violet-600 text-white' : 'text-neutral-400 hover:text-white'
              }`}
            >
              <Tv className="w-3.5 h-3.5" /> TV
            </button>
            <button
              onClick={() => switchType('movie')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                mediaType === 'movie' ? 'bg-violet-600 text-white' : 'text-neutral-400 hover:text-white'
              }`}
            >
              <Film className="w-3.5 h-3.5" /> Movies
            </button>
          </div>
        </div>
      </div>

      {/* Genre pills */}
      {genresLoading ? (
        <div className="flex gap-2 overflow-x-auto pb-3 mb-5" style={{ scrollbarWidth: 'none' }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-8 w-20 rounded-full bg-white/5 animate-pulse flex-shrink-0" />
          ))}
        </div>
      ) : (
        <div className="flex gap-2 overflow-x-auto pb-3 mb-5" style={{ scrollbarWidth: 'none' }}>
          <button
            onClick={() => setSelectedGenre(null)}
            className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
              !selectedGenre
                ? 'text-white'
                : 'bg-[#0A0A0A] border border-white/10 text-neutral-400 hover:text-white hover:border-white/20'
            }`}
            style={!selectedGenre ? { background: 'linear-gradient(135deg,#7c3aed,#4A9EFF)' } : {}}
          >
            All
          </button>
          {genres.map(g => (
            <button
              key={g.id}
              onClick={() => setSelectedGenre(g)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
                selectedGenre?.id === g.id
                  ? 'text-white'
                  : 'bg-[#0A0A0A] border border-white/10 text-neutral-400 hover:text-white hover:border-white/20'
              }`}
              style={selectedGenre?.id === g.id ? { background: 'linear-gradient(135deg,#7c3aed,#4A9EFF)' } : {}}
            >
              {g.name}
            </button>
          ))}
        </div>
      )}

      {/* Results grid */}
      {loading ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
          {Array.from({ length: 15 }).map((_, i) => (
            <div key={i} className="rounded-xl bg-white/5 animate-pulse" style={{ aspectRatio: '2/3' }} />
          ))}
        </div>
      ) : results.length === 0 ? (
        <div className="text-center py-20">
          <Compass className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
          <p className="text-neutral-500 text-sm">Nothing found — try another genre.</p>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
          {results.map((item, i) => (
            <Link
              key={item.tmdb_id}
              to={`/show/${item.tmdb_id}`}
              state={{ media_type: item.media_type }}
              className="group no-underline"
            >
              <div className="rounded-xl overflow-hidden bg-[#0A0A0A] border border-white/5 group-hover:border-violet-500/30 transition-all">
                {item.poster_url ? (
                  <img
                    src={item.poster_url}
                    alt={item.title}
                    className="w-full object-cover group-hover:scale-105 transition-transform duration-200"
                    style={{ aspectRatio: '2/3' }}
                  />
                ) : (
                  <div className="w-full bg-white/5 flex items-center justify-center" style={{ aspectRatio: '2/3' }}>
                    <Tv className="w-6 h-6 text-neutral-600" />
                  </div>
                )}
                <div className="p-2">
                  <p className="text-[11px] font-semibold text-white truncate leading-tight">{item.title}</p>
                  <div className="flex items-center justify-between mt-0.5">
                    {item.rating > 0 && (
                      <p className="text-[10px] text-neutral-600">⭐ {item.rating}</p>
                    )}
                    {item.year && (
                      <p className="text-[10px] text-neutral-700">{item.year}</p>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
        {hasMore && (
          <div className="flex justify-center mt-6">
            <button
              onClick={() => fetchResults(selectedGenre?.id || '', mediaType, page + 1, onlyMyServices)}
              disabled={loadingMore}
              className="px-6 py-2.5 rounded-full text-sm font-semibold border border-white/10 text-neutral-300 hover:border-white/20 hover:text-white disabled:opacity-50 transition-all flex items-center gap-2"
            >
              {loadingMore ? (
                <><div className="w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /> Loading…</>
              ) : 'Load more'}
            </button>
          </div>
        )}
        </>
      )}
    </div>
  );
}
